import { EventEmitter, Inject, Injectable, InjectionToken, Optional, inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { ConfigurationManager, JwtHelper, OnlineService, SerializabledDataType, TenantType, TokenType } from '@nts/std/utility';
import { LocalstorageHelper } from '@nts/std/utility';
import { BehaviorSubject, firstValueFrom, from, merge, Observable, of, Subject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { GenericServiceResponse } from '../responses/generic-service-response';
import { AuthTokenDto } from '../domain-models/dto/auth-token.dto';
import { UIStarter } from '../starter/ui-starter';
import { LogService } from '@nts/std/utility';
import { UserProfile } from '../domain-models/user/user-profile';
import { TenantProfile } from '../domain-models/license/tenant-profile';
import { classToPlain, ClassConstructor, plainToClass } from '@nts/std/serialization';
import { EnvironmentConfiguration } from '@nts/std/environments';
import { TenantTypes } from '../domain-models/license/tenant-types';
import { EnterpriseDataDto } from './dto/enterprise-data.dto';
import { EnterprisesListDto } from './dto/enterprise-list.dto';
import { Location } from '@angular/common';
import { DecodedTokenInterface } from '@nts/std/utility';
import { TelemetryService } from '@nts/std/telemetry';
import { CookiePreferencesDto } from '../domain-models/user/cookie-preferences-dto';
import { VersionManager } from '../resources/version-manager';
import { FrameworkServiceApiClient } from '../api-clients/framework-service-api-client';
import { HttpHeaders } from '@angular/common/http';
import { CurrentRouteService } from '../routing/current-route.service';
import { BaseError } from '../messages/base-error';
import { PresentationCache } from '../cache/presentation-cache';
import { AUTH_MOCK_MODE, AuthHeaders, AuthModelFullNames, AuthQueryParams, AuthRoutingPaths, AuthStorageKeys } from './auth.const';
import { AuthenticationTokenService } from './authenticazion-token.service';

let authServiceInstanceCounter = 0;

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    static ACCESS_TOKEN_KEY = AuthStorageKeys.ACCESS_TOKEN_KEY;
    static ACCESS_TOKEN_QUERY_KEY = AuthQueryParams.ACCESS_TOKEN_QUERY_KEY;
    static REFRESH_TOKEN_KEY = AuthStorageKeys.REFRESH_TOKEN_KEY;
    static REFRESH_TOKEN_QUERY_KEY = AuthQueryParams.REFRESH_TOKEN_QUERY_KEY;
    static CLEAR_AUTH_QUERY_KEY = AuthQueryParams.CLEAR_AUTH_QUERY_KEY;
    static USER_PROFILE = AuthStorageKeys.USER_PROFILE_KEY;
    static TENANT_PROFILE = AuthStorageKeys.TENANT_PROFILE_KEY;
    static COMPANY_ID_QUERY_KEY = AuthQueryParams.COMPANY_ID_QUERY_KEY;
    static TENANT_ID_QUERY_KEY = AuthQueryParams.TENANT_ID_QUERY_KEY;
    static ENTERPRISE_ID_QUERY_KEY = AuthQueryParams.ENTERPRISE_ID_QUERY_KEY;
    static USER_ID_QUERY_KEY = AuthQueryParams.USER_ID_QUERY_KEY;
    static LAST_USED_ENTERPRISE_DATA_KEY = AuthStorageKeys.LAST_USED_ENTERPRISE_DATA_KEY;

    static ANALYTICS_COOKIES_QUERY_KEY = AuthQueryParams.ANALYTICS_COOKIES_QUERY_KEY;
    static ANALYTICS_COOKIES_STORAGE_KEY = AuthStorageKeys.ANALYTICS_COOKIES_STORAGE_KEY;

    static ESSENTIAL_COOKIES_QUERY_KEY = AuthQueryParams.ESSENTIAL_COOKIES_QUERY_KEY;
    static ESSENTIAL_COOKIES_STORAGE_KEY = AuthStorageKeys.ESSENTIAL_COOKIES_STORAGE_KEY;

    static MINUTES_BEFORE_TOKEN_EXPIRE_FOR_RENEW = 5;

    // Alias path da utilizzare per fare riferimento alla rotta di login nel ms login service
    // Tutti i parametri passati in query string a questa rotta verranno inoltrati automaticamente alla vera rotta
    static LOGIN_ALIAS_PATH = AuthRoutingPaths.LOGIN_ALIAS_PATH;
    static LOGIN_CHOOSE_TENANT_ALIAS_PATH =  AuthRoutingPaths.LOGIN_CHOOSE_TENANT_ALIAS_PATH;
    static CHANGE_PASSWORD_ALIAS_PATH = AuthRoutingPaths.CHANGE_PASSWORD_ALIAS_PATH;

    static ENTERPRISE_DATA_HEADER_NAME = AuthHeaders.ENTERPRISE_DATA_HEADER_NAME;
    static AUTHORIZATION_HEADER_NAME = AuthHeaders.AUTHORIZATION_HEADER_NAME;

    static PROFILE_DOMAIN_MODEL_FULL_NAME = AuthModelFullNames.USER_PROFILE;

    onSessionExpired = new EventEmitter();
    onRefreshTokenChanged = new EventEmitter();
    onAccessTokenChanged = new EventEmitter<string>();
    onSessionRefreshingNeeded = new EventEmitter<string>();
    onSessionRefreshingError = new EventEmitter<GenericServiceResponse<AuthTokenDto>>();
    onSessionAcquire = new EventEmitter();
    onUserProfileRefreshingNeeded = new BehaviorSubject<boolean>(false);
    onUserProfileRefreshingError = new Subject<GenericServiceResponse<UserProfile>>();
    onTenantProfileRefreshingNeeded = new BehaviorSubject<boolean>(false);
    onTenantProfileRefreshingInProgress = new BehaviorSubject<boolean>(false);
    onTenantProfileRefreshingError = new Subject<GenericServiceResponse<TenantProfile>>();
    onUserProfileUpdate: Subject<UserProfile> = new Subject();
    onEnterpriseDataUpdate: Subject<EnterpriseDataDto> = new Subject();
    onTenantProfileUpdate: Subject<TenantProfile> = new Subject();
    enterpriseDataChanged: BehaviorSubject<boolean> = new BehaviorSubject(false);
    initialized$ = new BehaviorSubject<boolean>(false);

    private refreshTokenInterval = null;
    private currentRouteService: CurrentRouteService;
    private authenticationTokenService: AuthenticationTokenService = inject(AuthenticationTokenService);

    private _inIframe = false;
    get inIframe() {
        return this._inIframe;
    }

    constructor(
        private readonly environmentConfiguration: EnvironmentConfiguration,
        private readonly router: Router,
        private readonly location: Location,
        private readonly onlineService: OnlineService,
        private readonly telemetryService: TelemetryService,
        private readonly frameworkServiceApiClient: FrameworkServiceApiClient,
        @Optional()
        @Inject(AUTH_MOCK_MODE) private readonly authMockMode: boolean
    ) {
        authServiceInstanceCounter++;

        if (authServiceInstanceCounter > 1 && authMockMode !== true) {
            throw new Error('AuthService should be a singleton!');
        }

        // Recupera il servizio current route service
        this.currentRouteService = inject(CurrentRouteService);

        this.onAccessTokenChanged.subscribe(async (newAccessToken: string) => {
            if (newAccessToken?.length > 0 && this.checkTokenType(newAccessToken, TokenType.LoginSucceeded) && !this.authMockMode) {
                await this.checkUserProfileWithAccessToken(newAccessToken);
                await this.checkTenantProfileWithAccessToken(newAccessToken);
            }
        });

        if (authMockMode === true) {
            LocalstorageHelper.init(null, false, null, this.environmentConfiguration, true);
            this.setAccessToken('MOCK_ACCESS_TOKEN');
            this.setRefreshToken('MOCK_REFRESH_TOKEN');
        }

        this._inIframe = window.self != window.top;
    }

    async checkInitialization(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean> {

        if (!this.initialized$.value) {

            await this.setAuthDataFromQueryString(window.location.search);
            let accessToken: string = await this.getAccessToken();
            if (accessToken?.length > 0) {
                this.setupTokenRefresh();
            }

            const success = await this.waitUntilAccessTokenIsValidAndAvailable();
            let headers: HttpHeaders = null;

            if (success) {

                accessToken = await this.getAccessToken();

                if (accessToken?.length > 0) {
                    headers = new HttpHeaders().append('Authorization', 'Bearer ' + accessToken);
                }

                if (this.environmentConfiguration.isEnterpriseBarrierRequired) {
                    let enterprisesList = null;
                    if (!this.inIframe) {
                        const enterprisesListResponse: GenericServiceResponse<EnterprisesListDto> = await firstValueFrom(
                            this.frameworkServiceApiClient.getEnterprisesList({
                                bypass: false,
                                enableTimeout: true,
                                force: true
                            }, 0, headers)
                        );
                        if (enterprisesListResponse.operationSuccedeed) {
                            enterprisesList = enterprisesListResponse.result;
                        }
                        // } else if (enterprisesListResponse.operationSuccedeed === false) {
                        //     return false
                        // }
                    } else {
                        const enterprisesListResponse: GenericServiceResponse<EnterprisesListDto> = await firstValueFrom(
                            this.frameworkServiceApiClient.getEnterprisesList({
                                bypass: false,
                                enableTimeout: true,
                                timeoutMillisecond: 1000,
                            }, 0, headers)
                        );
                        if (enterprisesListResponse.operationSuccedeed) {
                            enterprisesList = enterprisesListResponse.result;
                        }
                    }
                    await this.initEnterpriseData(enterprisesList, route, state);
                } else {
                    // provo a settare l'enterprise con i dati presenti in query string se presenti,
                    // così il backend in caso riesce a utilizzarle come informazioni
                    await this.initEnterpriseData(null, route, state);
                }

                if (await this.tokenHasTenantId()) {
                    const success = await this.waitUntilTenantIsValidAndAvailable();
                    if (!success) {
                        return false;
                    }
                }
                await this.initCookiePreferences(headers);

            } else {
                return false;
            }

            this.initialized$.next(true);
        }
        return true;
    }

    private async checkUserProfileWithAccessToken(accessToken: string) {
        const decodedToken = this.decodeToken(accessToken);
        const userProfile: UserProfile = await this.getUserProfile();
        if (userProfile && decodedToken) {
            if (decodedToken.given_name !== userProfile.name) {
                this.onUserProfileRefreshingNeeded.next(true);
            } else if (decodedToken.family_name !== userProfile.lastName) {
                this.onUserProfileRefreshingNeeded.next(true);
            } else if (parseInt(decodedToken.sub) !== userProfile.id) {
                this.onUserProfileRefreshingNeeded.next(true);
            }
        }
    }

    private async checkTenantProfileWithAccessToken(accessToken: string) {
        const decodedToken = this.decodeToken(accessToken);
        const tenantProfile: TenantProfile = await this.getTenantProfile();
        if (tenantProfile && decodedToken) {
            if (parseInt(decodedToken.Tnt) !== tenantProfile.id) {
                this.onTenantProfileRefreshingNeeded.next(true);
            } else if (decodedToken.TntT === TenantType.Provider && tenantProfile.tenantType !== TenantTypes.Provider) {
                this.onTenantProfileRefreshingNeeded.next(true);
            } else if (decodedToken.TntT === TenantType.Partner && tenantProfile.tenantType !== TenantTypes.Partner) {
                this.onTenantProfileRefreshingNeeded.next(true);
            } else if (decodedToken.TntT === TenantType.EndUser && tenantProfile.tenantType !== TenantTypes.EndUser) {
                this.onTenantProfileRefreshingNeeded.next(true);
            }
        }
    }

    /**
     * Imposta prima l'access token e poi il refresh token nello storage
     *
     * @param accessToken       access token da impostare
     * @param refreshToken      refresh token da impostare
     */
    async setAuthData(accessToken: string, refreshToken: string): Promise<void> {
        if (accessToken != null) {
            await this.setAccessToken(accessToken);
        }
        if (refreshToken != null) {
            await this.setRefreshToken(refreshToken);
        }
    }

    /**
     * Imposta prima l'access token e poi il refresh token nello storage se sono presenti nei query params della query string passata
     *
     * @param queryString query string da analizzare
     */
    async setAuthDataFromQueryString(queryString: string): Promise<void> {
        const urlParams = new URLSearchParams(queryString);
        const accessToken = urlParams.get(AuthQueryParams.ACCESS_TOKEN_QUERY_KEY);
        const refreshToken = urlParams.get(AuthQueryParams.REFRESH_TOKEN_QUERY_KEY);
        await this.setAuthData(accessToken, refreshToken);
    }

    /**
     * Verifica il query string dello snapshot passato e setta automaticamente nello storage i token se presenti,
     * eventualmente pulisce i token se viene passato il parametro clear-auth.
     * Infine fa il redirect allo stesso url senza i query params sopra elencati.
     *
     * @param route snapshot della rotta da analizzare
     * @param state snapshot dello stato del router da analizzare
     */
    async checkQueryStringFromRoute(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<void> {
        const accessToken = route.queryParamMap.get(AuthQueryParams.ACCESS_TOKEN_QUERY_KEY);
        const refreshToken = route.queryParamMap.get(AuthQueryParams.REFRESH_TOKEN_QUERY_KEY);
        const clearAuthToken = route.queryParamMap.get(AuthQueryParams.CLEAR_AUTH_QUERY_KEY);

        await this.setAuthData(accessToken, refreshToken);

        if (accessToken != null && refreshToken != null) {

            // Remove access token, refresh token and clearAuthToken from query string
            const queryParams = route.queryParamMap.keys.reduce((obj, item) => {
                if (
                    item !== AuthQueryParams.ACCESS_TOKEN_QUERY_KEY &&
                    item !== AuthQueryParams.REFRESH_TOKEN_QUERY_KEY &&
                    item !== AuthQueryParams.CLEAR_AUTH_QUERY_KEY
                ) {
                    // TODO non stiamo gestendo la queryParamMap.getAll, per gli array di variabili
                    obj[item] = route.queryParamMap.get(item);
                }
                return obj;
            }, {});

            const urlWithoutQueryString = state.url.split('?')[0];

            if (clearAuthToken === 'true') {
                await this.clearAuthData();
            } else {
                this.setupTokenRefresh();
            }

            this.router.navigate([urlWithoutQueryString], {
                queryParams,
            });
        }
    }


    isUserAuthenticated(): Observable<boolean> {

        const isOnline = this.onlineService.isOnline;

        if (this.authMockMode === true || isOnline == false) {
            return of(true);
        }

        return from(this.getRefreshToken()).pipe(switchMap(async (refreshToken: string) => {

            if (!refreshToken) {
                return false;
            }
            const userIsValid = this.isTokenValid(refreshToken);
            if (userIsValid) {
                const userProfile = await this.getUserProfile();

                LogService.updateUserProfile(userProfile);
                this.telemetryService.updateUserProfile(userProfile);

                const userId = await this.getUserId(); // ritorna 0 se non esiste
                // Se il profilo utente non è presente nel local storage o
                // l'id presente nel token è diverso da quello dello user profile
                if (userProfile == null || userProfile?.id !== userId) {
                    this.onUserProfileRefreshingNeeded.next(true);
                }

                if (await this.tokenHasTenantId()) {
                    const tenantProfile = await this.getTenantProfile();

                    LogService.updateTenantProfile(tenantProfile);
                    this.telemetryService.updateTenantProfile(tenantProfile);

                    const tenantId = await this.getTenantId(); // ritorna 0 se non esiste
                    // Se il tenant non è presente nel local storage o
                    // l'id presente nel token è diverso da quello del tenant profile
                    if (tenantProfile == null || tenantProfile?.id !== tenantId) {
                        this.onTenantProfileRefreshingNeeded.next(true);
                    }
                }

                const accessToken = await this.getAccessToken();

                if (!accessToken) {
                    return firstValueFrom(this.waitRenewAccessToken());
                }

                const parsedAccessToken = await this.decodeToken(accessToken);

                if (!parsedAccessToken) {
                    return firstValueFrom(this.waitRenewAccessToken());
                }

                const expMinutesAccessToken = this.getTokenExpireInMinute(parsedAccessToken);

                // Se l'access token è scaduto o è in scadenza lo refresho (default < 5 minuti)
                if (expMinutesAccessToken < AuthService.MINUTES_BEFORE_TOKEN_EXPIRE_FOR_RENEW) {
                    return firstValueFrom(this.waitRenewAccessToken());
                }
            }
            return userIsValid;

        }));
    }

    /**
     * Ottiene lo stesso url con in query string i token.
     *
     * @param url url da modificare
     * @param addAccessToken devo aggiungere l'access token? @default true
     * @param addRefreshToken devo aggiungere il refresh token? @default true
     * @returns url con i token in query string
     */
    async getUrlWithTokens(url: string, addAccessToken: boolean = true, addRefreshToken = true): Promise<string> {

        const destinationUrl = new URL(url);

        if (addAccessToken) {
            const accessToken = await this.getAccessToken();
            destinationUrl.searchParams.set(AuthQueryParams.ACCESS_TOKEN_QUERY_KEY, accessToken);
        }

        if (addRefreshToken) {
            const refreshToken = await this.getRefreshToken();
            destinationUrl.searchParams.set(AuthQueryParams.REFRESH_TOKEN_QUERY_KEY, refreshToken);
        }

        return destinationUrl.toString();
    }

    /**
     * Recupera l'access token
     * @returns access token
     */
    protected async retrieveAccessToken(): Promise<SerializabledDataType> {
        return this.authenticationTokenService.retrieveAccessToken();
    }

    /**
     * Recupera l'access tokene presente nello storage.
     * Dopo averlo recuperato emette il Subject onAccessTokenRetrieved
     *
     * @returns l'access token presente nello storage
     */
    async getAccessToken(): Promise<string> {
        return await this.authenticationTokenService.getAccessToken();
    }



    /**
     * Fa il redirect alla rotta di login del ms login service per il cambio del tenant.
     * Se si passa il tenantId viene automaticamente selezionato il tenant corrispondente nel ms login service,
     * altrimenti viene mostrata la lista dei tenant disponibili.
     * Se addCorrentRouteToRedirectUrl è true viene aggiunta la rotta corrente alla redirect url.
     *
     * @param tenantId tenantId da passare in query string @default null
     * @param addCorrentRouteToRedirectUrl aggiunge la rotta corrente alla redirect url @default false
     * @returns Promise<void>
     */
    async changeTenant(
        tenantId: string | number = null,
        addCorrentRouteToRedirectUrl = false
    ): Promise<void> {

        // recupero l'url della login
        const baseUrl = await this.getLoginBaseUrl();

        // Delete the TENANT_ID_QUERY_KEY parameter.
        const redirectUrl = new URL(window.location.href);
        let params = new URLSearchParams(redirectUrl.search);
        params.delete(AuthQueryParams.TENANT_ID_QUERY_KEY);
        const encodedRedirectUrl = encodeURIComponent(redirectUrl.origin + redirectUrl.pathname + (params.toString()?.length > 0 ? ('?' + params.toString()) : ''));

        const destinationUrl = new URL(`${baseUrl}/auth/choose-tenant`);

        if (addCorrentRouteToRedirectUrl) {
            destinationUrl.searchParams.set('redirect-url', encodedRedirectUrl);
        }

        if (tenantId?.toString()?.length > 0) {
            destinationUrl.searchParams.set(AuthQueryParams.TENANT_ID_QUERY_KEY, tenantId.toString());
        }

        // effettuo il redirect
        UIStarter.startOtherDomainClient(destinationUrl.toString());

        // Attenzione non posso pulire prima del redirect altrimenti non riesco ad aggiungere in query string i token

        // Pulisco le informazioni di accesso
        await this.clearAuthData();
    }

    async getLoginBaseUrl(): Promise<string> {
        await PresentationCache.addIfNotExist(AuthModelFullNames.USER_PROFILE);
        const url = PresentationCache.get(AuthModelFullNames.USER_PROFILE)?.replace(new RegExp('user', 'g'), 'profile');
        return url?.split('/manage')[0];
    }

    /**
     * Recupera il refresh tokene presente nello storage.
     *
     * @returns il refresh token presente nello storage
     */
    async getRefreshToken(): Promise<string> {
        return await this.retrieveRefreshToken() as string;
    }

    /**
     * Salva un nuovo access token
     * @param newAccessToken nuovo access token da salvare
     * @returns true se il salvataggio è andato a buon fine, false altrimenti
     */
    protected async storeAccessToken(newAccessToken: string): Promise<boolean> {
        return await this.authenticationTokenService.storeAccessToken(newAccessToken);
    }

    /**
     * Imposta un nuovo access tokene nello storage.
     * Questo metodo emette i seguenti eventi:
     * - onAccessTokenChanged dopo che viene impostato il nuovo access token
     *
     * @param newAccessToken nuovo access token da impostare
     */
    async setAccessToken(newAccessToken: string): Promise<void> {
        if (newAccessToken != null) {
            this.authenticationTokenService.setAccessToken(newAccessToken);
            this.onAccessTokenChanged.emit(newAccessToken);
        }
    }

    async waitUntilAccessTokenIsValidAndAvailable(): Promise<boolean> {
        let success = true;
        const accessToken = await this.getAccessToken();
        const isTokenValid = accessToken != null && this.isTokenValid(accessToken);
        if (!isTokenValid) {
            await this.notifySessionRefreshingNeeded();
            success = await firstValueFrom(
                merge(
                    this.onSessionRefreshingError.pipe(map(_ => false)),
                    this.onAccessTokenChanged.pipe(map(_ => true)),
                ).pipe(
                    take(1), map((success) => success)
                )
            );
        }
        return success;
    }

    async waitUntilTenantIsValidAndAvailable(): Promise<boolean> {
        let success = true;
        const tenantProfile = await this.getTenantProfile();
        const isTenantValid = tenantProfile != null;
        if (!isTenantValid) {
            this.onTenantProfileRefreshingNeeded.next(true);
            success = await firstValueFrom(
                merge(
                    this.onTenantProfileRefreshingError.pipe(map(_ => false)),
                    this.onTenantProfileUpdate.pipe(map(_ => true)),
                ).pipe(
                    take(1), map((success) => success)
                )
            );
        }
        return success;
    }

    private internalEnterpriseList: EnterprisesListDto = null;

    async setEnterpriseList(enterpriseList: EnterprisesListDto) {
        this.internalEnterpriseList = enterpriseList;
    }

    async getEnterpriseList(): Promise<EnterprisesListDto> {
        return this.internalEnterpriseList;
    }

    /**
     * Recupera il valore di AnalyticsCookie da una stringa
     * @param sourceString
     */
    getAnalyticsCookiesFromString(sourceString: string): boolean {

        let result = false;

        // Recupero il valore di AnalyticsCookies dal query string
        const enterpriseRegexp = /analyticsCookies=([0-9]+)/;
        const enterpriseMatch = sourceString.match(enterpriseRegexp);
        if (enterpriseMatch && enterpriseMatch[1] != null) {
            result = enterpriseMatch[1].toLowerCase() === 'true';
        }
        return result;
    }

    /**
     * Recupera il valore di essentialCookies da una stringa
     * @param sourceString
     */
    getEssentialCookiesFromString(sourceString: string): boolean {

        let result = false;

        // Recupero il valore di EssentialCookies dal query string
        const enterpriseRegexp = /essentialCookies=([0-9]+)/;
        const enterpriseMatch = sourceString.match(enterpriseRegexp);
        if (enterpriseMatch && enterpriseMatch[1] != null) {
            result = enterpriseMatch[1].toLowerCase() === 'true';
        }
        return result;
    }

    #cookiePreferences: CookiePreferencesDto = null;

    get cookiePreferences(): CookiePreferencesDto {
        return this.#cookiePreferences;
    }

    storeSessionCookiePreferences(dto: CookiePreferencesDto) {
        window.sessionStorage.setItem(AuthStorageKeys.ANALYTICS_COOKIES_STORAGE_KEY, dto.analyticsCookies.toString());
        window.sessionStorage.setItem(AuthStorageKeys.ESSENTIAL_COOKIES_STORAGE_KEY, dto.essentialCookies.toString());
    }

    retrieveSessionCookiePreferences(): CookiePreferencesDto | null {
        const analyticsCookiesStorage = window.sessionStorage.getItem(AuthStorageKeys.ANALYTICS_COOKIES_STORAGE_KEY);
        const essentialCookiesStorage = window.sessionStorage.getItem(AuthStorageKeys.ESSENTIAL_COOKIES_STORAGE_KEY);
        if (analyticsCookiesStorage != null && essentialCookiesStorage != null) {
            const dto = new CookiePreferencesDto();
            dto.analyticsCookies = analyticsCookiesStorage.toLowerCase() === 'true';
            dto.essentialCookies = essentialCookiesStorage.toLowerCase() === 'true';
            return dto;
        }
        return null;
    }

    #initCookiePreferencesInProgress: Promise<void> = null;

    async initCookiePreferences(headers = new HttpHeaders()): Promise<void> {

        if (this.#initCookiePreferencesInProgress != null) {
            return await this.#initCookiePreferencesInProgress;
        }

        this.#initCookiePreferencesInProgress = new Promise<void>(async (resolve) => {
            let cookiePreferences = new CookiePreferencesDto();

            if (this.cookiePreferences != null) {
                cookiePreferences = this.cookiePreferences;
            } else {
                // Provo a recuperare le preferenze dai session storage
                cookiePreferences = this.retrieveSessionCookiePreferences();
            }

            const hasAnalyticsCookiesInQuery = window.location.href.includes(`${AuthQueryParams.ANALYTICS_COOKIES_QUERY_KEY}=`);
            const hasEssentialCookiesInQuery = window.location.href.includes(`${AuthQueryParams.ESSENTIAL_COOKIES_QUERY_KEY}=`);

            if (hasAnalyticsCookiesInQuery && hasEssentialCookiesInQuery) {

                cookiePreferences = new CookiePreferencesDto();
                cookiePreferences.analyticsCookies = this.getAnalyticsCookiesFromString(window.location.href) ?? false;
                cookiePreferences.essentialCookies = this.getEssentialCookiesFromString(window.location.href) ?? false;
                this.storeSessionCookiePreferences(cookiePreferences);

            } else if (cookiePreferences == null) {
                const accessToken = await this.getAccessToken();

                if (accessToken?.length > 0) {
                    headers = new HttpHeaders().append('Authorization', 'Bearer ' + accessToken);
                }

                try {

                    const cookiePreferencesResponse = await firstValueFrom(
                        this.frameworkServiceApiClient.getCookiePreferences(null, null, headers)
                    );

                    if (cookiePreferencesResponse?.operationSuccedeed) {
                        cookiePreferences = cookiePreferencesResponse.result;
                        this.storeSessionCookiePreferences(cookiePreferences);
                    }
                } catch (err) {
                    LogService.warn(err);
                }

            }

            this.#cookiePreferences = cookiePreferences;

            if (this.onlineService.isOnline === true) {
                if (cookiePreferences.essentialCookies) {

                    let tenantProfile = null;
                    if (await this.tokenHasTenantId()) {
                        tenantProfile = await this.getTenantProfile();
                    }

                    if (!this.telemetryService.initialized) {
                        let workerVersions = 'N/A';
                        if ('caches' in window) {
                            const workerVersionList = (await caches.keys()).filter(key => key.indexOf('assets:app:cache') > -1);
                            if (workerVersionList?.length > 0) {
                                workerVersions = workerVersionList.join(' - ');
                            }
                        }

                        this.telemetryService.init(
                            ConfigurationManager?.Current?.config?.appInsights?.connectionString,
                            ConfigurationManager?.Current?.config?.appInsights?.enableAutoRouteTracking,
                            {
                                ...VersionManager.Current.version,
                                workerVersions
                            },
                            cookiePreferences.analyticsCookies,
                            tenantProfile ? tenantProfile.businessName : undefined
                        );
                    } else {
                        this.telemetryService.setCanUseAuthenticatedUserContext = cookiePreferences.analyticsCookies;
                    }

                    this.telemetryService.setAuthenticatedUserContext(
                        await this.getUserProfile(),
                        tenantProfile
                    );

                    if (!cookiePreferences.analyticsCookies) {
                        this.telemetryService.clearAuthenticatedUserContext();
                    }
                } else {
                    this.telemetryService.clearAuthenticatedUserContext();
                }
            }
            resolve();
        });

        await this.#initCookiePreferencesInProgress;
        this.#initCookiePreferencesInProgress = null;
    }

    async initEnterpriseData(
        enterprisesList: EnterprisesListDto | null,
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ) {

        await this.setEnterpriseList(enterprisesList);

        if (enterprisesList?.enterprises?.length >= 0) {

            const hasEnterpriseIdInQuery = window.location.href.includes(`${AuthQueryParams.ENTERPRISE_ID_QUERY_KEY}=`);
            const hasCompanyIdInQuery = window.location.href.includes(`${AuthQueryParams.COMPANY_ID_QUERY_KEY}=`);
            const currentEnterpriseData = await this.getEnterpriseData(await this.getTenantId());
            const lastUsedEnterpriseData: EnterpriseDataDto = await LocalstorageHelper.getStorageItem(AuthStorageKeys.LAST_USED_ENTERPRISE_DATA_KEY, undefined, undefined, false) as EnterpriseDataDto;

            let enterpriseIdParsedFromQueryString: number = 0;
            let companyIdParsedFromQueryString: number = 0;

            // Se ho dei parametri in query string verifico la coerenza con la lista delle enterprise
            if (hasEnterpriseIdInQuery) {

                // Recupero il codice dell'enterprise dal query string
                enterpriseIdParsedFromQueryString = this.getEnterpiseIdFromString(window.location.href);

                // let foundEnterprise = null;

                // if (enterpriseIdParsedFromQueryString > -1) {

                //     // Verifico enterprise
                //     foundEnterprise = enterprisesList?.enterprises?.find((e) =>
                //         e.enterpriseId === enterpriseIdParsedFromQueryString
                //     );
                // }

                // if (
                //     foundEnterprise ||
                //     this.inIframe // se sono dentro un iframe non posso ricercare nella lista enterprise perchè non viene popolata
                // ) {

                if (hasCompanyIdInQuery) {
                    companyIdParsedFromQueryString = this.getCompanyIdFromString(window.location.href);
                }



                //     let foundCompany = null;
                //     if (companyIdParsedFromQueryString > -1) {

                //         // Verifico company
                //         foundCompany = foundEnterprise?.companies?.find((c) =>
                //             c.companyId === companyIdParsedFromQueryString
                //         );
                //     }

                //     if (foundEnterprise?.companies?.length === 0) {
                //         // Se l'azienda trovata non ha company resetto il company id
                //         // companyIdParsedFromQueryString = -1;
                //     } else if (!foundCompany) {
                //         // companyIdParsedFromQueryString = -1;
                //     }
                // }

            }

            // // se valogono entrambi 0 vuol dire che non sono stati passati quindi devo dedurre in automatico dallo storage l'enterprise selezionata
            // if (enterpriseIdParsedFromQueryString !== -1 || companyIdParsedFromQueryString !== -1) {
            //     const dto = new EnterpriseDataDto();
            //     dto.enterpriseId = enterpriseIdParsedFromQueryString;
            //     dto.companyId = companyIdParsedFromQueryString;

            //     if (currentEnterpriseData?.enterpriseId > 0) {
            //          if (dto.enterpriseId != currentEnterpriseData.enterpriseId || dto.companyId != currentEnterpriseData.companyId) {
            //             // E' cambiato l'enterprise data dal refresh precedente
            //             this.enterpriseDataChanged.next(true);
            //          }
            //     }

            //     await this.setEnterpriseData(dto, await this.getTenantId());
            if (enterpriseIdParsedFromQueryString > 0) {
                // se enterprise id è passato

                const dto = new EnterpriseDataDto();

                if (enterprisesList?.enterprises?.length > 0) {
                    // se ho delle enterprise devo verificare la corettezza dei dati

                    // Verifico enterprise
                    const foundEnterprise = enterprisesList?.enterprises?.find((e) =>
                        e.enterpriseId === enterpriseIdParsedFromQueryString
                    );

                    if (foundEnterprise) {

                        dto.enterpriseId = foundEnterprise.enterpriseId;

                        // Verifico company
                        if (companyIdParsedFromQueryString > 0) {
                            // company passata

                            // Verifico company
                            const foundCompany = foundEnterprise?.companies?.find((e) =>
                                e.companyId === companyIdParsedFromQueryString
                            );

                            if (foundCompany) {
                                dto.companyId = companyIdParsedFromQueryString;
                            } else {
                                // company non trovata
                                dto.companyId = -1;
                            }

                        } else {
                            // company non passata
                            dto.companyId = 0;

                            // se esistono delle company nell'azienda trovata devo restituire errorre di ditta non trovata
                            if (foundEnterprise?.companies?.length > 0) {

                                // company non trovata
                                dto.companyId = -1;
                            }
                        }

                    } else {
                        //enterprise non trovata
                        dto.enterpriseId = -1;
                    }

                } else {
                    // se non ho le enterprise
                    dto.enterpriseId = enterpriseIdParsedFromQueryString;

                    if (companyIdParsedFromQueryString > 0) {
                        dto.companyId = companyIdParsedFromQueryString;
                    }
                }

                await this.setEnterpriseData(dto, await this.getTenantId());
            } else if (currentEnterpriseData?.enterpriseId > 0 && enterprisesList?.enterprises?.find((e) => e.enterpriseId === currentEnterpriseData?.enterpriseId)) {
                // utilizzo il valore salvato nel session storage
                await this.setEnterpriseData(currentEnterpriseData, await this.getTenantId());
            } else if (lastUsedEnterpriseData?.enterpriseId > 0 && enterprisesList?.enterprises?.find((e) => e.enterpriseId === lastUsedEnterpriseData?.enterpriseId)) {
                // utilizzo il valore salvato nel local storage
                await this.setEnterpriseData(lastUsedEnterpriseData, await this.getTenantId());
            } else if (enterprisesList?.enterprises?.length > 0) {
                // utilizzo il valore di defualt di enterprise list, disponibile solo se enterprise list è popolato
                const dto = new EnterpriseDataDto();
                const defaultEnteprise = enterprisesList.enterprises.find(e => e.isDefault === true);
                if (defaultEnteprise) {
                    dto.enterpriseId = defaultEnteprise.enterpriseId;
                    if (defaultEnteprise.companies?.length > 0) {
                        const defaultCompany = defaultEnteprise.companies.find((c) => c.isDefault);
                        if (defaultCompany) {
                            dto.companyId = defaultCompany.companyId;
                        }

                        // } else {
                        //     dto.companyId = defaultEnteprise.companies[0].companyId;
                        // }
                    }

                    await this.setEnterpriseData(dto, await this.getTenantId());
                }
            }

        }
        const newEnterpriseData = await this.getEnterpriseData(await this.getTenantId());

        if (newEnterpriseData?.enterpriseId > 0) {
            const enterpriseId = route.queryParamMap.get(AuthQueryParams.ENTERPRISE_ID_QUERY_KEY);
            const companyId = route.queryParamMap.get(AuthQueryParams.COMPANY_ID_QUERY_KEY);

            if (parseInt(enterpriseId) != newEnterpriseData?.enterpriseId || (companyId != null && (parseInt(companyId) != newEnterpriseData?.companyId))) {
                // const urlWithoutQueryString = state.url.split('?')[0];
                //
                // // Remove access token, refresh token and clearAuthToken from query string
                // let queryParams = route.queryParamMap.keys.reduce((obj, item) => {
                //     if (
                //         item !== AuthQueryParams.ENTERPRISE_ID_QUERY_KEY &&
                //         item !== AuthQueryParams.COMPANY_ID_QUERY_KEY
                //     ) {
                //         // TODO non stiamo gestendo la queryParamMap.getAll, per gli array di variabili
                //         obj[item] = route.queryParamMap.get(item);
                //     }
                //     return obj;
                // }, {});

                // const tenantId = await this.getTenantId();

                // queryParams = {
                //     ...queryParams,
                //     [AuthService.TENANT_ID_QUERY_KEY]: tenantId.toString(),
                //     [AuthService.ENTERPRISE_ID_QUERY_KEY]: newEnterpriseData?.enterpriseId.toString(),
                // };

                // if ( newEnterpriseData?.companyId > 0) {
                //     queryParams[AuthService.COMPANY_ID_QUERY_KEY]= newEnterpriseData?.companyId.toString()
                // }

                // this.router.navigate([urlWithoutQueryString], {
                //     queryParams
                // });

                const dto = new EnterpriseDataDto();
                dto.enterpriseId = newEnterpriseData?.enterpriseId;
                dto.companyId = newEnterpriseData?.companyId;
                await this.setEnterpriseData(dto, await this.getTenantId());
            }


        } else {
            // Cancello tutte le informazioni presenti sull'enterprise data
            await this.clearEnterpriseData(await this.getTenantId());

            // se esistono delle informazioni dell'enterprise in query string le imposto
            // il backend le sfrutterà parsando poi l'header
            const hasEnterpriseIdInQuery = window.location.href.includes(`${AuthQueryParams.ENTERPRISE_ID_QUERY_KEY}=`);
            if (hasEnterpriseIdInQuery) {

                // Recupero il codice dell'enterprise dal query string
                const enterpriseIdParsedFromQueryString = this.getEnterpiseIdFromString(window.location.href);

                const companyIdParsedFromQueryString = this.getCompanyIdFromString(window.location.href);

                if (enterpriseIdParsedFromQueryString > -1) {
                    const dto = new EnterpriseDataDto();
                    dto.enterpriseId = enterpriseIdParsedFromQueryString;
                    if (companyIdParsedFromQueryString > -1) {
                        dto.companyId = companyIdParsedFromQueryString;
                    }
                    await this.setEnterpriseData(dto, await this.getTenantId());
                }
            }
        }

        const urlWithoutQueryString = state.url.split('?')[0];

        // Remove access token, refresh token and clearAuthToken from query string
        let queryParams = route.queryParamMap.keys.reduce((obj, item) => {
            if (
                item !== AuthQueryParams.ENTERPRISE_ID_QUERY_KEY &&
                item !== AuthQueryParams.COMPANY_ID_QUERY_KEY
            ) {
                // TODO non stiamo gestendo la queryParamMap.getAll, per gli array di variabili
                obj[item] = route.queryParamMap.get(item);
            }
            return obj;
        }, {});

        const tenantId = await this.getTenantId();

        queryParams = {
            ...queryParams,
        };
        if (tenantId > 0) {
            queryParams[AuthQueryParams.TENANT_ID_QUERY_KEY] = tenantId.toString();

            const enterpriseData = await this.getEnterpriseData(tenantId);

            if (enterpriseData?.enterpriseId > 0) {
                queryParams[AuthQueryParams.ENTERPRISE_ID_QUERY_KEY] = enterpriseData?.enterpriseId.toString();
            }

            if (enterpriseData?.companyId > 0) {
                queryParams[AuthQueryParams.COMPANY_ID_QUERY_KEY] = enterpriseData?.companyId.toString();
            }

            const queryStringData = new URLSearchParams(queryParams);
            queryStringData.delete(AuthQueryParams.ACCESS_TOKEN_QUERY_KEY);
            queryStringData.delete(AuthQueryParams.REFRESH_TOKEN_QUERY_KEY);
            const queryString = queryStringData.toString();
            this.location.go(urlWithoutQueryString + '?' + queryString);
            // this.router.navigate([urlWithoutQueryString], {
            //     queryParams
            // });
        }
    }

    /**
     * Recupera il codice numerico della company da una stringa
     * @param sourceString
     * @returns ritorna -1 se non trova il codice altrimenti il suo valore
     */
    getCompanyIdFromString(sourceString: string): number {
        let result = -1;

        // Recupero il codice della company dal query string
        const companyRegexp = /companyId=([0-9]+)/;
        const companyMatch = sourceString.match(companyRegexp);
        if (companyMatch && companyMatch[1] != null) {
            result = parseInt(companyMatch[1]);
        }
        return result;
    }

    /**
     * Recupera il codice numerico della enterprise da una stringa
     * @param sourceString
     * @returns ritorna -1 se non trova il codice altrimenti il suo valore
     */
    getEnterpiseIdFromString(sourceString: string): number {

        let result = -1;

        // Recupero il codice dell'enterprise dal query string
        const enterpriseRegexp = /enterpriseId=([0-9]+)/;
        const enterpriseMatch = sourceString.match(enterpriseRegexp);
        if (enterpriseMatch && enterpriseMatch[1] != null) {
            result = parseInt(enterpriseMatch[1]);
        }
        return result;
    }

    async getEnterpriseDataError(): Promise<BaseError | null> {
        if (!this.environmentConfiguration.isEnterpriseBarrierRequired) {
            return null;
        }
        const enterpriseList = await this.getEnterpriseList();
        if (!enterpriseList?.enterprises || enterpriseList?.enterprises.length === 0) {
            const error = new BaseError();
            error.description = 'Nessuna azienda associata al tenant corrente';
            error.code = 'EnterpriseListNotFound';
            return error;
        }

        const enterpriseData = await this.getEnterpriseData(await this.getTenantId());
        if (!enterpriseData) {
            const error = new BaseError();
            error.description = 'Azienda non trovata';
            error.code = 'EnterpriseNotFound';
            return error;
        }

        if (
            // enterprise non trovata
            enterpriseData?.enterpriseId === -1 ||

            // enterprise non passata
            enterpriseData?.enterpriseId === 0
        ) {
            const error = new BaseError();
            error.description = 'Azienda non trovata';
            error.code = 'EnterpriseNotFound';
            return error;
        }

        if (
            // companyId non trovato
            enterpriseData?.companyId === -1
        ) {
            const error = new BaseError();
            error.description = 'Ditta non trovata';
            error.code = 'CompanyNotFound';
            return error;
        }

        return null;
    }

    /**
     * Recupera l'enterprise corrente salvata nella session per il tenant corrente
     *
     * @param tenantId
     * @returns
     */
    async getEnterpriseData(tenantId: number): Promise<EnterpriseDataDto> {
        return this.authenticationTokenService.getEnterpriseData(tenantId);
    }

    /**
     * Imposta l'enterprise corrente nella session per il tenant corrent
     *
     * @param enterpriseData        enterprise data da salvare nella session
     * @param tenantId              id del tenant corrente
     */
    async setEnterpriseData(enterpriseData: EnterpriseDataDto, tenantId: number): Promise<void> {
        await this.authenticationTokenService.setEnterpriseData(enterpriseData, tenantId);
        this.onEnterpriseDataUpdate.next(enterpriseData);
    }

    /**
     * Pulisce l'enterprise corrente nella session per il tenant corrent
     *
     * @param enterpriseData        enterprise data da salvare nella session
     * @param tenantId              id del tenant corrente
     */
    async clearEnterpriseData(tenantId: number): Promise<void> {
        await this.authenticationTokenService.clearEnterpriseData(tenantId);
        this.onEnterpriseDataUpdate.next(null);
    }

    /**
     * Salva il profilo utente
     * @param userProfile oggetto plain js contenente le proprietà del profilo utente
     */
    protected async storeUserProfile(userProfile: Record<string, any>) {
        await this.authenticationTokenService.storeUserProfile(userProfile);
    }

    /**
     * Recupera il profilo utente salvato
     * @returns oggetto plain js contenente le proprietà del profilo utente
     */
    protected async retrieveUserProfile(): Promise<Record<string, any>> {
        return this.authenticationTokenService.retrieveUserProfile();
    }

    async setUserProfile(user: UserProfile): Promise<void> {
        const plainUser = classToPlain(user, { strategy: 'excludeAll' });
        await this.storeUserProfile(plainUser);
        LogService.updateUserProfile(user);
        this.telemetryService.updateUserProfile(user);
        this.onUserProfileUpdate.next(user);
    }

    /**
     * Recupera il profilo utente salvato
     * @returns oggetto UserProfile con le proprietà del profilo utente
     */
    async getUserProfile(): Promise<UserProfile> {
        const plainUser = await this.retrieveUserProfile();
        return plainToClass<UserProfile, object>(UserProfile as ClassConstructor<UserProfile>, plainUser as object, { strategy: 'excludeAll' });
    }

    /**
     * Salva il profilo tenant
     * @param tenantProfile oggetto plain js contenente le proprietà del profilo tenant
     */
    protected async storeTenantProfile(tenantProfile: Record<string, any>): Promise<boolean> {
        return this.authenticationTokenService.storeTenantProfile(tenantProfile);
    }

    async setTenantProfile(tenant: TenantProfile): Promise<void> {
        const plainTenant = classToPlain(tenant, { strategy: 'excludeAll' });
        await this.storeTenantProfile(plainTenant);
        LogService.updateTenantProfile(tenant);
        this.telemetryService.updateTenantProfile(tenant);
        this.onTenantProfileUpdate.next(tenant);
    }

    /**
     * Recupera il profilo tenant salvato
     * @returns oggetto plain js contenente le proprietà del profilo tenant
     */
    protected async retrieveTenantProfile(): Promise<Record<string, any>> {
        return this.authenticationTokenService.retrieveTenantProfile();
    }

    async getTenantProfile(): Promise<TenantProfile> {
        const plainUser = await this.retrieveTenantProfile();
        return plainToClass<TenantProfile, object>(
            TenantProfile as ClassConstructor<TenantProfile>, plainUser as object, { strategy: 'excludeAll' });
    }

    async setRefreshToken(newRefreshToken: string): Promise<void> {
        if (newRefreshToken != null) {
            await this.storeRefreshToken(newRefreshToken);
            this.onRefreshTokenChanged.emit();
        }
    }

    /**
     * Salva il token di refresh
     * @param newRefreshToken nuovo refresh token da salvare
     * @returns true se il salvataggio è andato a buon fine, false altrimenti
     */
    protected async storeRefreshToken(newRefreshToken: string): Promise<boolean> {
        return this.authenticationTokenService.storeRefreshToken(newRefreshToken);
    }

    /**
     * Recupera il token di refresh
     * @returns refresh token
     */
    protected async retrieveRefreshToken(): Promise<SerializabledDataType> {
        return this.authenticationTokenService.retrieveRefreshToken();
    }

    notifySessionExpired(): void {
        this.onSessionExpired.emit();
    }

    async notifySessionRefreshingNeeded(): Promise<void> {
        this.onSessionRefreshingNeeded.emit(await this.getRefreshToken());
    }

    getTokenExpireInMinute(token: DecodedTokenInterface): number {
        return this.authenticationTokenService.getTokenExpireInMinute(token);
    }

    async tokenHasTenantId(): Promise<boolean> {
        return this.authenticationTokenService.tokenHasTenantId();
    }

    async getUserName(accessToken: string = null): Promise<string> {
        return this.authenticationTokenService.getUserName(accessToken);
    }

    async getUserId(accessToken: string = null): Promise<number> {
        return this.authenticationTokenService.getUserId(accessToken);
    }

    async getTenantId(accessToken: string = null): Promise<number> {
        return this.authenticationTokenService.getTenantId(accessToken);
    }

    logIn(): void {
        UIStarter.redirectToLogin();
    }

    changePassword(blank = false): void {
        UIStarter.redirectToChangePassword(blank);
    }

    async clearAuthData(): Promise<void> {
        await this.authenticationTokenService.clearAuthData();
        this.telemetryService.clearProfiles();
    }

    checkTokenHasTenant(token: string): boolean {
        const parsedToken = this.decodeToken(token);
        return parsedToken?.Tnt?.length > 0;
    }

    logOut(): void {
        this.clearAuthData();
        UIStarter.redirectToLogin(true, true);
    }

    setTokenData(accessData: { access_token: string, refresh_token: string; }): Observable<boolean> {
        return from(this.setAccessToken(accessData.access_token)).pipe(switchMap((a) =>
            from(this.setRefreshToken(accessData.refresh_token)).pipe(map(() =>
                true
            ))
        ));
    }

    isTokenValid(token: string): boolean {
        return this.authenticationTokenService.isTokenValid(token);
    }

    checkTokenType(token: string, tokenType: TokenType): boolean {
        return this.authenticationTokenService.checkTokenType(token, tokenType);
    }

    checkTokenTenantType(token: string, tenantType: TenantType): boolean {
        return this.authenticationTokenService.checkTokenTenantType(token, tenantType);
    }

    decodeToken(token: string): DecodedTokenInterface | null {
        return this.authenticationTokenService.decodeToken(token);
    }

    async getCurrentUserId(token?: string): Promise<number> {
        return this.authenticationTokenService.getCurrentUserId(token);
    }

    private async checkAndRenewAccessTokenExpire(): Promise<void> {

        const isOnline = this.onlineService.isOnline;

        if (this.authMockMode === true || isOnline == false) {
            return;
        }

        const accessToken = await this.getAccessToken();
        const refreshToken = await this.getRefreshToken();

        const parsedAccessToken = this.decodeToken(accessToken);
        const parsedRefreshToken = this.decodeToken(refreshToken);

        if (parsedAccessToken != null) {

            const expMinutesAccessToken = this.getTokenExpireInMinute(parsedAccessToken);
            const expMinutesRefreshToken = this.getTokenExpireInMinute(parsedRefreshToken);

            // if 5 min (default) before expire renew access token
            if (expMinutesAccessToken < AuthService.MINUTES_BEFORE_TOKEN_EXPIRE_FOR_RENEW) {
                if (expMinutesRefreshToken > 0) {
                    LogService.info('Access Token is expiring in ' + expMinutesAccessToken.toFixed(0) + ' minutes. It\'s time to renew.');
                    LogService.log('Refresh Token is expiring in ' + expMinutesRefreshToken.toFixed(0) + ' minutes.');
                    this.notifySessionRefreshingNeeded();

                    merge(
                        this.onSessionRefreshingError.pipe(map(_ => false)),
                        this.onAccessTokenChanged.pipe(map(_ => true)),
                    ).pipe(
                        take(1)).subscribe((success) => {
                            if (!success) {
                                this.notifySessionExpired();
                            }
                        });

                } else {
                    this.notifySessionExpired();
                }
            }
        }
    }

    private waitRenewAccessToken(): Observable<boolean> {
        this.notifySessionRefreshingNeeded();
        return merge(
            this.onSessionRefreshingError.pipe(map(_ => false)),
            this.onAccessTokenChanged.pipe(map(_ => true)),
        ).pipe(
            take(1), map((success) => success));
    }

    private setupTokenRefresh(): void {
        // this.createRefresTokenFrame();
        // ogni min verifca il token
        if (this.refreshTokenInterval != null) {
            clearInterval(this.refreshTokenInterval);
        }
        // ogni min verifca il token
        this.refreshTokenInterval = setInterval(async () => {

            // Se sono in una rotta autenticata
            if (this.currentRouteService.isAuthenticatedRoute) {
                this.checkAndRenewAccessTokenExpire();
            }
        }, 60000);
    }
}
